import {NavigateOptions} from 'react-router-dom';

import {EntityResourceIds} from '@dms/api/shared';
import {vehiclesRoutes} from '@dms/routes';
import {queryParams} from '@dms/shared';

import {composePath, isSafeUrl, useNavigate, useQueryState} from 'shared/web';

export interface FormNavigateConfig {
  replaceOnReturn?: boolean;
  navigateOptions?: NavigateOptions;
}

export const useFormNavigate = (config?: FormNavigateConfig) => {
  const navigate = useNavigate();
  const [returnUrl] = useQueryState(queryParams.RETURN_URL);
  const [purchaseType] = useQueryState(queryParams.VEHICLE_PURCHASE_TYPE);
  const [referrer] = useQueryState(queryParams.REDIRECT_SOURCE);
  const [referrerId] = useQueryState('id');

  const navigateToReturnUrlIfPresent = (): boolean => {
    if (returnUrl && isSafeUrl(returnUrl)) {
      const baseOptions = config?.navigateOptions ?? {};
      const options = {
        ...baseOptions,
        replace: config?.replaceOnReturn ?? false,
      } as NavigateOptions;
      navigate(returnUrl, options);
      return true;
    }
    return false;
  };

  const navigateOnConfirm = (createdRecordId?: string) => {
    if (navigateToReturnUrlIfPresent()) {
      return;
    }

    // Default: navigate to vehicle detail if we have an id, otherwise fallback to vehicle list
    if (createdRecordId) {
      navigate(
        composePath(vehiclesRoutes.detail, {params: {id: createdRecordId}}),
        config?.navigateOptions
      );
      return;
    }

    navigate(vehiclesRoutes.home, config?.navigateOptions);
  };

  const navigateOnDiscard = () => {
    if (navigateToReturnUrlIfPresent()) {
      return;
    }

    // Default: try to go back within the SPA (React Router sets state.idx starting at 0).
    // Be defensive: treat non-numeric/undefined idx as no in-app history.
    const idx = (window.history.state as {idx?: unknown} | null | undefined)?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(vehiclesRoutes.home, config?.navigateOptions);
  };

  const getReferrer = () => referrer as EntityResourceIds | undefined;
  const getReferrerId = () => referrerId;
  const getPurchaseTypeFromQuery = () => purchaseType;

  const isBusinessCaseReferrer = getReferrer() === EntityResourceIds.businessCase;

  return {
    navigateOnConfirm,
    navigateOnDiscard,
    getReferrer,
    isBusinessCaseReferrer,
    getReferrerId,
    getPurchaseTypeFromQuery,
  };
};
